import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { MUSIC_VIDEO_MAX_CUSTOM_TEXTS } from 'fym-shared/src/music24/musicVideoDoc';
import { LONG_VIDEO_END_CARD_SEC } from 'fym-shared/src/music24/story/longVideoEndCard';
import { TypedMemo } from '../../common/types';
import { newDocId } from '../doc/clipIds';
import { addCustomText, toggleFixedText, toggleSongInfoCorner } from '../doc/textOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { textSpans } from '../preview/previewClock';
import { formatTime } from '../timeline/timelineGeometry';
import { EndCardFields } from './EndCardFields';
import { ToggleItem } from './ToggleItem';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Text panel (design d2): the title, the name, the end card and the song info corner, each with
 * a switch; the texts the user adds; the end card line and link. A click on a row selects it on the
 * timeline and in the inspector.
 */
const TextPanelComponent: React.FC = () => {
  const intl = useIntl();
  const { doc, data, songMap, change, select, selection, transport, readOnly } = useMusicVideoEditor();
  const { texts } = doc;
  const spans = useMemo(() => textSpans(doc.settings, songMap.downbeats), [doc.settings, songMap.downbeats]);
  const selectedKey = selection?.kind === 'text' ? selection.key : null;
  const title = texts.title.text === '' ? data.song.title : texts.title.text;
  const artist = texts.name.text === '' ? data.song.artistName : texts.name.text;
  const span = (from: number, to: number, text: string) =>
    intl.formatMessage(panelMessages.spanFacts, { from: formatTime(from), to: formatTime(to), text });
  const addText = () => {
    const id = newDocId('t');
    const text = intl.formatMessage(panelMessages.newText);
    change((current) => addCustomText(current, { id, text, atSec: transport.livePositionSec() }));
    select({ kind: 'text', key: `custom:${id}` });
  };

  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(panelMessages.textTitle)}</h3>
      <div className={styles.column}>
        <ToggleItem
          title={intl.formatMessage(panelMessages.titleItem)}
          facts={span(spans.title.startSec, spans.title.endSec, title)}
          isOn={texts.title.on}
          isSelected={selectedKey === 'title'}
          disabled={readOnly}
          onToggle={() => change((current) => toggleFixedText(current, 'title'))}
          onSelect={() => select({ kind: 'text', key: 'title' })}
        />
        <ToggleItem
          title={intl.formatMessage(panelMessages.nameItem)}
          facts={span(spans.name.startSec, spans.name.endSec, `${artist} — ${data.song.title}`)}
          isOn={texts.name.on}
          isSelected={selectedKey === 'name'}
          disabled={readOnly}
          onToggle={() => change((current) => toggleFixedText(current, 'name'))}
          onSelect={() => select({ kind: 'text', key: 'name' })}
        />
        <ToggleItem
          title={intl.formatMessage(panelMessages.endCardItem)}
          facts={
            spans.endCard === null
              ? intl.formatMessage(panelMessages.endFactsShort)
              : intl.formatMessage(panelMessages.endFacts, { seconds: LONG_VIDEO_END_CARD_SEC })
          }
          isOn={texts.endCard.on}
          isSelected={selectedKey === 'endCard'}
          disabled={readOnly}
          onToggle={() => change((current) => toggleFixedText(current, 'endCard'))}
          onSelect={() => select({ kind: 'text', key: 'endCard' })}
        />
        <ToggleItem
          title={intl.formatMessage(panelMessages.cornerItem)}
          facts={intl.formatMessage(panelMessages.cornerFacts)}
          isOn={texts.songInfoCorner}
          disabled={readOnly}
          onToggle={() => change(toggleSongInfoCorner)}
        />
        {texts.custom.map((item) => (
          <button
            key={item.id}
            type="button"
            className={styles.cutRow}
            onClick={() => select({ kind: 'text', key: `custom:${item.id}` })}
          >
            {item.text}
            <small>
              {intl.formatMessage(panelMessages.customFacts, {
                from: formatTime(item.startSec),
                to: formatTime(item.endSec),
              })}
            </small>
          </button>
        ))}
      </div>
      <button
        type="button"
        className={styles.ghostButton}
        disabled={readOnly || texts.custom.length >= MUSIC_VIDEO_MAX_CUSTOM_TEXTS}
        onClick={addText}
      >
        {intl.formatMessage(panelMessages.addText, { time: formatTime(transport.positionSec) })}
      </button>
      <div className={styles.separator} />
      <EndCardFields />
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(panelMessages.textHint)}</p>
    </>
  );
};

export const TextPanel = TypedMemo(TextPanelComponent);
